import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { SystemNotice } from '../../models/systemNotice/systemNotice';
import { getSystemNoticeMeta } from '../../content/systemNoticeTypes';
import { helperService } from '../../services/helper.service';

interface SystemNoticesDialogProps {
  open: boolean;
  notices: SystemNotice[];
  initialIndex?: number;
  onClose: () => void;
  onRead?: (notice: SystemNotice) => void;
}

type PaginationItem = number | 'left-ellipsis' | 'right-ellipsis';

const MAX_VISIBLE_PAGE_BUTTONS = 5;

const buildPaginationItems = (total: number, current: number): PaginationItem[] => {
  if (total <= MAX_VISIBLE_PAGE_BUTTONS) {
    return Array.from({ length: total }, (_value, index) => index);
  }

  const windowSize = MAX_VISIBLE_PAGE_BUTTONS;
  let start = Math.max(0, current - Math.floor(windowSize / 2));
  let end = start + windowSize - 1;

  if (end > total - 1) {
    end = total - 1;
    start = end - windowSize + 1;
  }

  const items: PaginationItem[] = [];
  if (start > 0) {
    items.push('left-ellipsis');
  }

  for (let i = start; i <= end; i++) {
    items.push(i);
  }

  if (end < total - 1) {
    items.push('right-ellipsis');
  }

  return items;
};

const SystemNoticesDialog: React.FC<SystemNoticesDialogProps> = ({
  open,
  notices,
  initialIndex = 0,
  onClose,
  onRead,
}) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setIndex(Math.min(Math.max(initialIndex, 0), Math.max(notices.length - 1, 0)));
    }
  }, [open, initialIndex, notices.length]);

  const safeIndex = Math.min(Math.max(index, 0), Math.max(notices.length - 1, 0));
  const paginationItems = useMemo(
    () => buildPaginationItems(notices.length, safeIndex),
    [notices.length, safeIndex],
  );

  if (!open || notices.length === 0) {
    return null;
  }

  const notice = notices[safeIndex];
  const meta = getSystemNoticeMeta(notice.type);
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === notices.length - 1;
  const hasMultiple = notices.length > 1;

  const goPrev = () => {
    if (!isFirst) setIndex(safeIndex - 1);
  };

  const goNext = () => {
    if (!isLast) setIndex(safeIndex + 1);
  };

  const handleRead = () => {
    onRead?.(notice);
    if (isLast) {
      onClose();
    }
    else {
      setIndex(safeIndex + 1);
    }
  };

  const bodyHtml = helperService.bbCodeToHtml(notice.body);

  return (
    <Dialog
      className="dialog"
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      maxWidth="md"
      aria-labelledby="system-notice-title"
    >
      <Tooltip title="Sluiten">
        <IconButton
          aria-label="Sluiten"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 2 }}
        >
          <Icon>close</Icon>
        </IconButton>
      </Tooltip>

      <DialogTitle
        id="system-notice-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, color: meta.color, px: 3, pr: 7 }}
      >
        <Icon sx={{ color: meta.color }}>{meta.icon}</Icon>
        <span>{notice.title}</span>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {/* Body is bbcode uit de backend, geconverteerd naar veilige HTML in helperService. */}
        <Typography component="div" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 2, px: 3, pb: 2 }}>
        <Tooltip title="Ik heb de melding gelezen, ga door naar het volgende bericht">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRead}
          >
            <Icon sx={{ mr: 1 }}>bookmark</Icon>
            Gelezen
          </Button>
        </Tooltip>

        {hasMultiple && (
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" flexWrap="nowrap">
            {!isFirst && (
              <Tooltip title="Vorige">
                <IconButton
                  onClick={goPrev}
                  aria-label="Vorige melding"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    color: 'inherit',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Icon>chevron_left</Icon>
                </IconButton>
              </Tooltip>
            )}

            {paginationItems.map((item) => {
              if (item === 'left-ellipsis' || item === 'right-ellipsis') {
                return (
                  <Typography key={item} component="span" sx={{ px: 0.5, fontSize: '16px', lineHeight: 1 }}>
                    ...
                  </Typography>
                );
              }

              const isActive = item === safeIndex;
              return (
                <Tooltip key={`page-${item}`} title={`Ga naar melding ${item + 1}`}>
                  <IconButton
                    onClick={() => setIndex(item)}
                    aria-label={`Melding ${item + 1}`}
                    aria-current={isActive}
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      fontSize: '16px',
                      borderRadius: '50%',
                      backgroundColor: isActive ? 'action.selected' : 'transparent',
                      color: 'inherit',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {item + 1}
                  </IconButton>
                </Tooltip>
              );
            })}

            {!isLast && (
              <Tooltip title="Volgende">
                <IconButton
                  onClick={goNext}
                  aria-label="Volgende melding"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    color: 'inherit',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Icon>chevron_right</Icon>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SystemNoticesDialog;
